
import { useState, useCallback } from 'react';
import { AiResponse, MessageType } from '@/types/assistant';
import { v4 as uuidv4 } from 'uuid';

export const useMessageFlow = ({
  currentSessionId,
  userId,
  currentTopic,
  messages,
  addUserMessage,
  addAiMessage,
  startMessagePerformance,
  finishMessagePerformance,
  recoverFromError,
  processAiRequest,
  validateAndIncrementUsage
}: any) => {

  const addMessage = useCallback((message: MessageType) => {
    console.log('Adding message:', message);
  }, []);

  const updateMessage = useCallback((id: string, updates: Partial<MessageType>) => {
    console.log('Updating message:', id, updates);
  }, []);

  const removeMessage = useCallback((id: string) => {
    console.log('Removing message:', id);
  }, []);

  const createAiMessage = useCallback((aiResponse: AiResponse, sessionId: string, userId: string): MessageType => {
    return {
      id: uuidv4(),
      content: aiResponse.content,
      role: 'assistant',
      timestamp: new Date(),
      session_id: sessionId,
      user_id: userId,
      suggestions: aiResponse.suggestions || [],
      model: aiResponse.model,
      metadata: {
        knowledgeUsed: aiResponse.knowledgeUsed,
        knowledgeResults: aiResponse.knowledgeResults,
        requestId: aiResponse.requestId,
        usage: aiResponse.usage
      },
      feedback: null,
      is_streaming: false,
    };
  }, []);

  const createMessageMetadata = useCallback((requestId: string) => {
    return {
      knowledgeUsed: false,
      knowledgeResults: [],
      suggestionsSource: null,
      contextTruncated: false,
      queryExpansionUsed: false,
      expandedQueries: [],
      usage: {
        promptTokens: 0,
        completionTokens: 0,
        totalTokens: 0
      },
      processingTime: 0,
      completedAt: Date.now(),
      streaming: false,
      requestId,
      startTime: Date.now()
    };
  }, []);

  // FIXED: Build proper conversation history from existing messages + new user input
  const buildConversationHistory = useCallback((userContent: string) => {
    console.log('[MESSAGE FLOW FIX] Building conversation history:', {
      existingMessagesCount: Array.isArray(messages) ? messages.length : 0,
      userContentLength: userContent.length,
      sessionId: currentSessionId
    });

    // Convert existing messages to conversation history format
    const conversationHistory = (Array.isArray(messages) ? messages : [])
      .filter(msg => msg.content && msg.content.trim().length > 0) // Only include messages with content
      .map(msg => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content
      }));

    // Add the new user message
    conversationHistory.push({
      role: 'user' as const,
      content: userContent
    });

    console.log('[MESSAGE FLOW FIX] Conversation history built:', {
      totalMessages: conversationHistory.length,
      lastMessage: conversationHistory[conversationHistory.length - 1],
      conversationHistory: conversationHistory.map(msg => ({
        role: msg.role,
        contentLength: msg.content.length
      }))
    });

    return conversationHistory;
  }, [messages, currentSessionId]);

  const handleMessageFlow = useCallback(async (options: any) => {
    // FIXED: Generate requestId at the very beginning
    const requestId = options.requestId || uuidv4();
    
    try {
      console.log('[MESSAGE FLOW FIX] Starting message flow with requestId:', {
        content: options.content,
        sessionId: currentSessionId,
        userId,
        topic: currentTopic,
        requestId
      });
      
      if (!currentSessionId || !userId) {
        throw new Error('Missing session or user ID');
      }

      // FIXED: Validate usage with proper requestId before processing
      console.log('[MESSAGE FLOW FIX] Validating usage with requestId:', requestId);
      const usageValidation = await validateAndIncrementUsage(currentTopic, requestId);
      
      if (!usageValidation.allowed) {
        console.error('[MESSAGE FLOW FIX] Usage limit exceeded:', {
          requestId,
          topic: currentTopic,
          reason: usageValidation.reason
        });
        throw new Error('Usage limit exceeded');
      }

      console.log('[MESSAGE FLOW FIX] Usage validation passed:', {
        requestId,
        topic: currentTopic,
        allowed: usageValidation.allowed
      });

      // Add user message first
      const userMessage = await addUserMessage(options.content, currentSessionId, userId);
      console.log('[MESSAGE FLOW FIX] User message added:', { 
        messageId: userMessage.id,
        requestId
      });

      // FIXED: Build conversation history properly
      const conversationHistory = buildConversationHistory(options.content);
      
      // FIXED: Process AI request with proper message format and requestId
      const aiResponse = await processAiRequest({
        content: options.content,
        messages: conversationHistory, // CRITICAL: Pass conversation history as messages array
        sessionId: currentSessionId,
        userId,
        topic: currentTopic,
        requestId, // FIXED: Pass the generated requestId
        includeMultiLanguage: options.includeMultiLanguage || false,
        mode: options.mode || 'conversation'
      });

      console.log('[MESSAGE FLOW FIX] AI request processed:', {
        requestId,
        hasResponse: !!aiResponse,
        responseSuccess: aiResponse?.success
      });

    } catch (error) {
      console.error('[MESSAGE FLOW FIX] Error in message flow:', {
        error: error instanceof Error ? error.message : String(error),
        requestId
      });
      recoverFromError(error, 'message_flow', requestId);
    }
  }, [currentSessionId, userId, currentTopic, validateAndIncrementUsage, addUserMessage, buildConversationHistory, processAiRequest, recoverFromError]);

  return {
    messages,
    addMessage,
    updateMessage,
    removeMessage,
    createAiMessage,
    createMessageMetadata,
    handleMessageFlow
  };
};
